import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import moment from 'moment';
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { VALIDATION_REGEXS } from 'UI/utils';

export const ATTACHMENT_FIELD_KEYS = ['file_24', 'file_25', 'file_26'];

const EMAIL_KEYS = ['work_email', 'email'];
const LEGACY_INTERVIEW_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const LEGACY_INTERVIEW_TYPE_TIME_MAP = {
  fixedHour: {
    id: 1,
    title: 'Fixed Hour'
  },
  timeRange: {
    id: 2,
    title: 'Time Range'
  }
};
const LEGACY_INTERVIEW_TYPE_ID_MAP = {
  faceToFace: 1,
  remoteVideo: 2
};

const normalizeEmail = email => email.trim().toLowerCase();

const extractEmail = item => {
  if (typeof item === 'string') return item.trim();
  if (!item || typeof item !== 'object') return '';

  const emailKey = EMAIL_KEYS.find(key => typeof item[key] === 'string' && item[key].trim());

  return emailKey ? item[emailKey].trim() : '';
};

export const mapCandidateAttachments = files =>
  (files || []).map(file => ({
    ...file,
    file_type_id: file.file_type_id ?? file.fileType?.id
  }));

export const buildEmailList = (items = []) => {
  if (!Array.isArray(items)) return [];

  const seenEmails = new Set();

  return items.reduce((emails, item) => {
    const email = extractEmail(item);

    if (!email || !VALIDATION_REGEXS.EMAIL.test(email)) return emails;

    const normalizedEmail = normalizeEmail(email);

    if (seenEmails.has(normalizedEmail)) return emails;

    seenEmails.add(normalizedEmail);

    return [...emails, email];
  }, []);
};

export const buildAttachments = (_, formValues = {}) => {
  const allAttachments = ATTACHMENT_FIELD_KEYS.flatMap(fieldKey => formValues?.[fieldKey] || []);

  return {
    candidate: [...allAttachments],
    sendout: [...allAttachments],
    toSend: [...allAttachments]
  };
};

export const mapCancelEmailToSendEmail = value => !value;

export const buildNumberValue = value => {
  if (value == null || value === '') return null;

  const parsedValue = Number(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const normalizeLegacyTimeZoneName = value => {
  if (!value || typeof value !== 'string') return null;

  return value
    .split('/')
    .map(part =>
      part.length <= 2 ? part.toUpperCase() : `${part.charAt(0)}${part.slice(1).toLowerCase()}`
    )
    .join('/');
};

export const buildLegacyInterviews = (_, formValues = {}) => {
  const interviewTypeTimeId = formValues?.interviewTimeFrame?.id;
  const legacyInterviewTypeTime = LEGACY_INTERVIEW_TYPE_TIME_MAP[interviewTypeTimeId];
  const legacyInterviewTypeId = LEGACY_INTERVIEW_TYPE_ID_MAP[formValues?.interviewType];
  const interviewDate = formValues?.interviewDate;
  const interviewTimeZone = normalizeLegacyTimeZoneName(formValues?.interviewTimezone?.name);

  if (!legacyInterviewTypeTime || !legacyInterviewTypeId || !interviewDate || !interviewTimeZone) {
    return [];
  }

  const isTimeRangeInterview = interviewTypeTimeId === 'timeRange';

  return [
    {
      interview_type_time: legacyInterviewTypeTime,
      interview_type_id: legacyInterviewTypeId,
      interview_date: moment(interviewDate).format(LEGACY_INTERVIEW_DATE_FORMAT),
      interview_time_zone: interviewTimeZone,
      interview_schedule: isTimeRangeInterview,
      hour_fixed: isTimeRangeInterview ? null : formValues?.interviewTime || null,
      interview_range: isTimeRangeInterview
        ? {
            from: formValues?.interviewFromHour || null,
            to: formValues?.interviewToHour || null
          }
        : null
    }
  ];
};

export const checkIfCandidateIsAlreadyInSendout = async ({ candidateId, jobOrderId }) => {
  if (!candidateId || !jobOrderId) {
    return { isDuplicate: false, duplicates: [], error: null };
  }

  try {
    const params = { job_order_id: jobOrderId, candidate_id: candidateId };
    const queryParams = new URLSearchParams(params).toString();
    const response = await API.get(`${Endpoints.SendoutDuplicates}?${queryParams}`);

    if (response.status !== HTTPStatusCodes.Ok) {
      return { isDuplicate: false, duplicates: [], error: new Error('Duplicate check failed') };
    }

    const duplicates = Array.isArray(response.data?.data) ? response.data?.data : [];
    return { isDuplicate: duplicates.length > 0, duplicates };
  } catch (error) {
    return { isDuplicate: false, duplicates: [], error };
  }
};
